import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Button, Typography } from "@mui/material";
import { Chip, DataTable, Drawer } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import {
  COMPANY_ACCOUNTS,
  COMPANY_ACCOUNT,
  COMPANY_ROUTING,
} from "graphql/companyConsole";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { Stack } from "components/layout/reusableLayouts";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import Role from "enums/Role";
import Summary from "components/common/Summary";
import { ConsoleError } from "./ConsoleState";
import { FinancialAccount, ConsoleProps, instant, label, money } from "./types";

export function CompanyAccounts({
  companyId,
  params,
  navigate,
  showLedger = false,
}: ConsoleProps & { showLedger?: boolean }) {
  const { t } = useTranslation();
  const page = useCompanyConnection<FinancialAccount>({
    query: COMPANY_ACCOUNTS,
    field: "companyFinancialAccounts",
    variables: {
      companyId,
      filter: {
        currency: params.get("currency") || undefined,
        issuer: params.get("issuer") || undefined,
        financialAccountId: params.get("financialAccount") || undefined,
      },
    },
  });
  const id = params.get("account");
  const detail = useQuery<{ companyFinancialAccount: FinancialAccount }>(
    COMPANY_ACCOUNT,
    {
      variables: { companyId, financialAccountId: id },
      skip: !id,
      fetchPolicy: "no-cache",
    },
  );
  const account = detail.data?.companyFinancialAccount;
  const canReadRouting = useCheckUserRole(Role.Admin);
  const [routingAccount, setRoutingAccount] = useState<string | null>(null);
  const routing = useQuery<{
    companyRoutingDetails: {
      ach: {
        accountNumber: string;
        routingNumber: string;
        bankName?: string;
        accountHolderName?: string;
      };
    };
  }>(COMPANY_ROUTING, {
    variables: { companyId, financialAccountId: id },
    skip: !id || routingAccount !== id || !canReadRouting,
    fetchPolicy: "no-cache",
  });
  const columns: DataColumn<FinancialAccount>[] = [
    {
      id: "name",
      header: t("Financial account"),
      cell: (row) => ({ content: row.name, caption: row.id }),
    },
    {
      id: "issuer",
      header: t("Issuer / program"),
      cell: (row) => ({
        content: row.issuer ?? t("Unavailable"),
        caption: label(row.accountType),
      }),
    },
    {
      id: "status",
      header: t("Status"),
      cell: (row) => <Chip tone="neutral">{label(row.status)}</Chip>,
    },
    {
      id: "available",
      header: t("Available"),
      align: "right",
      cell: (row) => money(row.balance?.available, row.currency),
    },
    {
      id: "incoming",
      header: t("Pending incoming funds"),
      align: "right",
      cell: (row) => money(row.balance?.pending, row.currency),
    },
    {
      id: "updated",
      header: t("As of"),
      cell: (row) => instant(row.balance?.reportedAt),
    },
  ];
  return (
    <Box ref={page.viewportRef} sx={{ flex: 1, minHeight: 0 }}>
      {page.error && <ConsoleError retry={page.retry} />}
      <ConsoleContinue paused={page.paused} loadMore={page.loadMore} />
      <DataTable
        columns={columns}
        rows={page.rows}
        getRowKey={(row) => row.id}
        loading={page.loading}
        isFetchingMore={page.isFetchingMore}
        onScroll={page.handleScroll}
        stickyHeader
        emptyState={t("No financial accounts found")}
        onRowClick={(row) => navigate({ account: row.id })}
      />
      <Drawer
        open={!!id}
        onClose={() => {
          setRoutingAccount(null);
          navigate({ account: null });
        }}
        title={account?.name ?? t("Financial account")}
        loading={detail.loading}
      >
        {detail.error ? (
          <ConsoleError />
        ) : (
          account && (
            <Stack gap="var(--space-5)">
              {account.stale && (
                <Alert severity="warning">
                  {t("This balance is an older issuer reading")}
                </Alert>
              )}
              {!account.available && (
                <Alert severity="warning">
                  {t("The issuer balance is currently unavailable")}
                </Alert>
              )}
              <Summary
                disableContainer
                items={[
                  { title: t("Account ID"), value: account.id },
                  {
                    title: t("Issuer"),
                    value: account.issuer ?? t("Unavailable"),
                  },
                  { title: t("Status"), value: label(account.status) },
                  {
                    title: t("Currency"),
                    value: account.currency ?? t("Unavailable"),
                  },
                  {
                    title: t("Available"),
                    value: money(account.balance?.available, account.currency),
                  },
                  {
                    title: t("Pending incoming funds"),
                    value: money(account.balance?.pending, account.currency),
                  },
                  ...(account.balance?.creditLimit != null
                    ? [
                        {
                          title: t("Credit limit"),
                          value: money(
                            account.balance.creditLimit,
                            account.currency,
                          ),
                        },
                        {
                          title: t("Outstanding"),
                          value: money(
                            account.balance.outstanding,
                            account.currency,
                          ),
                        },
                      ]
                    : []),
                  {
                    title: t("Balance as of"),
                    value: instant(account.balance?.reportedAt),
                  },
                ]}
              />
              {showLedger && (
                <Button
                  variant="default-outlined"
                  onClick={() =>
                    navigate({
                      tab: "ledger",
                      ledgerAccount: account.id,
                      account: null,
                    })
                  }
                >
                  {t("View ledger")}
                </Button>
              )}
              {canReadRouting &&
                account.capabilities.includes("readRoutingDetails") && (
                  <>
                    <Button
                      variant="default-outlined"
                      onClick={() =>
                        setRoutingAccount(
                          routingAccount === account.id ? null : account.id,
                        )
                      }
                    >
                      {t(
                        routingAccount === account.id
                          ? "Hide funding details"
                          : "View funding details",
                      )}
                    </Button>
                    {routingAccount === account.id && (
                      <>
                        {routing.loading && (
                          <Typography role="status">
                            {t("Loading funding details")}
                          </Typography>
                        )}
                        {routing.error && <ConsoleError />}
                        {routing.data && (
                          <Summary
                            disableContainer
                            items={Object.entries(
                              routing.data.companyRoutingDetails.ach,
                            )
                              .filter(
                                ([key, value]) => key !== "__typename" && value,
                              )
                              .map(([key, value]) => ({
                                title: label(key),
                                value,
                              }))}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
            </Stack>
          )
        )}
      </Drawer>
    </Box>
  );
}
